import React, { useCallback, useMemo, useRef } from "react";
import type {
    ColDef,
    CustomCellRendererProps,
    GetContextMenuItemsParams,
    GetRowIdFunc,
    GridApi,
    GridOptions,
    GridReadyEvent,
    GridState,
    IsFullWidthRowParams,
    MenuItemDef,
    SelectionOptions,
    StateUpdatedEvent,
} from "@/ui/molecules/Grid";
import { DealNameCellRenderer } from "./DealNameCellRenderer";
import { StatusCellRenderer } from "./StatusCellRenderer";
import { UploadFileCellRenderer } from "./UploadFileCellRenderer";
import Box from "@/ui/atoms/Box";
import { MUIGlobalStyles as GlobalStyles } from "@proda-ai/ui-kit";
import Grid, { GridLoaderSkeleton } from "@/ui/molecules/Grid";
import type {
    DealExtractionInfo,
    AssetType,
    DealInfoWithParent,
    DealExtractionInfoWithParent,
    AddFileRow,
    ExtractionType,
} from "../dealTypes";
import { assetTypes } from "../dealTypes";
import type { UserType } from "../userTypes";
import { canDeleteExtraction, canDeleteFile } from "../userTypes";
import { Button, Icon, IconBox } from "@proda-ai/ui-kit";
import Typography from "@/ui/atoms/Typography";
import EmptyGridMessageRenderer, {
    type EmptyGridMessageRendererProps,
} from "@/ui/molecules/EmptyGridMessageRenderer";
import { isNullOrUndefined } from "@/utils/guards";

const INTERNAL_ERROR_MESSAGE =
    "There was an error fetching projects, Please try refreshing the page.";
const GRID_EMPTY_MESSAGE = "No projects found.";
const GRID_EMPTY_SUBMESSAGE = "Sorry, we couldn't find any matching projects";

export const isAddFileRowTypeGuard = (
    dealExtractionInfo: DealExtractionInfo,
): dealExtractionInfo is AddFileRow => {
    return "addFile" in dealExtractionInfo;
};

const isExtractionInfoWithParentTypeGuard = (
    dealExtractionInfo: DealExtractionInfo,
): dealExtractionInfo is DealExtractionInfoWithParent => {
    return "parentDealName" in dealExtractionInfo;
};

const isDealInfoWithParentTypeGuard = (
    dealExtractionInfo: DealExtractionInfo,
): dealExtractionInfo is DealInfoWithParent => {
    return "isParent" in dealExtractionInfo;
};

export const testId = "DealTable-autosaved-message";

export type DealGridState = Pick<
    GridState,
    | "columnSizing"
    | "filter"
    | "sort"
    | "columnOrder"
    | "columnVisibility"
    | "partialColumnState"
>;

type DealTableProps = {
    suppressNoRowOverlay?: boolean;
    rowData: DealExtractionInfo[];
    containerHeight?: string;
    savedGridState?: DealGridState;
    saveGridStatus: "idle" | "resolved" | "rejected";
    dealsIsError: boolean;
    dealSearchPhrase: string | null;
    userType: UserType | undefined;
    getDealUrl: ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => string;
    getExtractionUrl: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => string;
    getRentRollExtractionUrl: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => string;
    onViewDeal: (params: { projectId: number; databaseId: number }) => void;
    onViewExtraction: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => void;
    onViewRentRoll?: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) => void;
    onAddFile: (params: { projectId: number; databaseId: number }) => void;
    onDealDelete: (params: {
        projectId: number;
        databaseId: number;
    }) => Promise<void>;
    onExtractionDelete: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) => Promise<void>;
    onExport: ({
        extractions,
        projectId,
        databaseId,
    }: {
        extractions: {
            extractionId: number;
            extractionType: ExtractionType;
        }[];
        projectId: number;
        databaseId: number;
    }) => void;
    onResumeDraft?: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) => void;
    resetViewButtonClicked: () => void;
    saveGridState: (gridState: DealGridState) => void;
    ComponentFormattedDate: React.ComponentType<{ date: string }>;
};

export const DealTable: React.FC<DealTableProps> = ({
    suppressNoRowOverlay,
    rowData,
    containerHeight = "80vh",
    savedGridState,
    saveGridStatus,
    dealsIsError,
    dealSearchPhrase,
    userType,
    getDealUrl,
    getExtractionUrl,
    getRentRollExtractionUrl,
    onViewDeal,
    onViewExtraction,
    onViewRentRoll,
    onAddFile,
    onDealDelete,
    onExtractionDelete,
    onExport,
    onResumeDraft,
    resetViewButtonClicked,
    saveGridState,
    ComponentFormattedDate = (props: { date: string }) => {
        return props.date;
    },
}) => {
    const gridApi = useRef<GridApi | null>(null);
    const containerStyle = {
        width: "100%",
        height: containerHeight,
        margin: "0 auto",
    };

    const DateCellRenderer = React.useMemo(
        // eslint-disable-next-line react/no-unstable-nested-components, react/display-name
        () => (props: CustomCellRendererProps<DealExtractionInfo, string>) => {
            return (
                props.value !== null &&
                props.value !== undefined && (
                    <ComponentFormattedDate date={props.value} />
                )
            );
        },
        [ComponentFormattedDate],
    );

    const getContextMenuItemsForDeal = useCallback(
        (data: DealInfoWithParent) => {
            const viewDeal = {
                name: "View",
                action: () => {
                    !isNullOrUndefined(data.projectId) &&
                        onViewDeal({
                            projectId: data.projectId,
                            databaseId: data.databaseId,
                        });
                },
            };
            const addDocumentToDeal = {
                name: "Add document",
                action: () =>
                    !isNullOrUndefined(data.projectId) &&
                    onAddFile({
                        projectId: data.projectId,
                        databaseId: data.databaseId,
                    }),
            };

            const deleteDeal = {
                name: "Delete",
                disabled: data.numFiles > 0,
                action: async () =>
                    !isNullOrUndefined(data.projectId) &&
                    onDealDelete({
                        projectId: data.projectId,
                        databaseId: data.databaseId,
                    }),
                cssClasses: ["delete-item"],
            };

            const adminOnlyActions = ["separator", deleteDeal];
            const allowDeletion = canDeleteFile(userType, data);

            return [
                viewDeal,
                addDocumentToDeal,
                ...(allowDeletion ? adminOnlyActions : []),
            ];
        },
        [onAddFile, onDealDelete, onViewDeal, userType],
    );

    const getContextMenuItemsForExtraction = useCallback(
        (data: DealExtractionInfoWithParent) => {
            const isDraftExtraction = data.status === "draft";

            const viewExtraction = {
                name: "View",
                action: () => {
                    // Check if this is a rent roll extraction by looking for rent roll specific fields
                    const isRentRollExtraction =
                        data.extractionType === "rent_roll_extraction" ||
                        (data.excelsiorFileId !== null &&
                            data.excelsiorFileId !== undefined &&
                            data.sheetId !== undefined);

                    if (!isRentRollExtraction) {
                        onViewExtraction({
                            projectId: data.projectId,
                            databaseId: data.databaseId,
                            extractionId: data.extractionId,
                            extractionStatus:
                                data.status === "completed"
                                    ? "completed"
                                    : "draft",
                        });
                    } else if (
                        data.excelsiorFileId !== null &&
                        data.excelsiorFileId !== undefined
                    ) {
                        window.open(
                            getRentRollExtractionUrl({
                                fileId: data.excelsiorFileId,
                                extractionId: Math.abs(data.extractionId),
                                sheetId: `s${data.sheetId}`,
                                databaseId: data.databaseId,
                            }),
                            "_self",
                        );
                    } else {
                        // Overview row has no file identity; resolve then navigate.
                        onViewRentRoll?.({
                            projectId: data.projectId,
                            databaseId: data.databaseId,
                            extractionId: data.extractionId,
                        });
                    }
                },
            };
            const resumeDraftExtraction = {
                name: "Resume",
                action: () => {
                    // Check if this is a rent roll extraction by looking for rent roll specific fields
                    const isRentRollExtraction =
                        data.extractionType === "rent_roll_extraction" ||
                        (data.excelsiorFileId !== null &&
                            data.excelsiorFileId !== undefined &&
                            data.sheetId !== undefined);

                    if (
                        isRentRollExtraction &&
                        data.excelsiorFileId !== null &&
                        data.excelsiorFileId !== undefined
                    ) {
                        window.open(
                            getRentRollExtractionUrl({
                                fileId: data.excelsiorFileId,
                                extractionId: Math.abs(data.extractionId),
                                sheetId: `s${data.sheetId}`,
                                databaseId: data.databaseId,
                            }),
                            "_self",
                        );
                    } else {
                        onResumeDraft &&
                            onResumeDraft({
                                projectId: data.projectId,
                                databaseId: data.databaseId,
                                extractionId: data.extractionId,
                            });
                    }
                },
                disabled: !onResumeDraft,
            };
            const discardDraftExtraction = {
                name: "Discard",
                action: () =>
                    data.extractionId &&
                    onExtractionDelete({
                        projectId: data.projectId,
                        databaseId: data.databaseId,
                        extractionId: data.extractionId,
                    }),
                cssClasses: ["delete-item"],
            };
            const deleteExtraction = {
                name: "Delete",
                action: async () =>
                    data.extractionId &&
                    onExtractionDelete({
                        projectId: data.projectId,
                        databaseId: data.databaseId,
                        extractionId: data.extractionId,
                    }),
                cssClasses: ["delete-item"],
            };
            const exportExtraction = {
                name: "Export",
                action: () =>
                    data.extractionId &&
                    data.extractionType &&
                    onExport({
                        extractions: [
                            {
                                extractionId: data.extractionId,
                                extractionType: data.extractionType,
                            },
                        ],
                        projectId: data.projectId,
                        databaseId: data.databaseId,
                    }),
            };

            const adminOnlyActionsDraft = ["separator", discardDraftExtraction];
            const adminOnlyActionsCompleted = ["separator", deleteExtraction];
            const allowDiscard = canDeleteExtraction(userType, data);
            const allowDeletion = canDeleteFile(userType, data);

            if (isDraftExtraction) {
                return [
                    resumeDraftExtraction,
                    exportExtraction,
                    ...(allowDiscard ? adminOnlyActionsDraft : []),
                ];
            } else {
                return [
                    viewExtraction,
                    exportExtraction,
                    ...(allowDeletion ? adminOnlyActionsCompleted : []),
                ];
            }
        },
        [
            onViewExtraction,
            onViewRentRoll,
            onResumeDraft,
            onExtractionDelete,
            onExport,
            userType,
            getRentRollExtractionUrl,
        ],
    );

    const getContextMenuItems = useCallback(
        (
            params: GetContextMenuItemsParams<DealExtractionInfo>,
        ): (string | MenuItemDef)[] => {
            const isDealNameColumn =
                params.column?.getColId() === "ag-Grid-AutoColumn";

            if (!isDealNameColumn) return [];

            const data = params.node?.data;

            if (
                isDealNameColumn &&
                data &&
                isDealInfoWithParentTypeGuard(data)
            ) {
                return getContextMenuItemsForDeal(data);
            } else if (
                isDealNameColumn &&
                data &&
                isExtractionInfoWithParentTypeGuard(data)
            ) {
                return getContextMenuItemsForExtraction(data);
            } else {
                return [];
            }
        },
        [getContextMenuItemsForDeal, getContextMenuItemsForExtraction],
    );

    const colDefs = useMemo<ColDef[]>(
        () => [
            {
                headerName: "Project Name",
                field: "dealName",
                rowGroup: true,
                hide: true,
            },
            {
                headerName: "ID",
                field: "projectId",
                maxWidth: 70,
            },
            { headerName: "Company & Database", field: "databaseCompanyName" },
            {
                headerName: "Property Name",
                field: "propertyName",
                tooltipValueGetter: params => {
                    const data = params.data as DealExtractionInfo | undefined;
                    if (
                        !data ||
                        !isDealInfoWithParentTypeGuard(data) ||
                        data.properties.length < 2
                    ) {
                        return undefined;
                    }
                    return data.properties.map(p => p.propertyName).join("\n");
                },
                valueGetter: params => {
                    const data = params.data as DealExtractionInfo | undefined;
                    if (!data || !isDealInfoWithParentTypeGuard(data)) {
                        return "";
                    }
                    const [first, ...rest] = data.properties;
                    if (!first || rest.length < 1) {
                        return data.propertyName ?? "";
                    }
                    return `${first.propertyName} +${rest.length} more`;
                },
            },
            {
                headerName: "Asset Type",
                field: "assetType",
                valueFormatter: params =>
                    assetTypes[params.value as AssetType] || params.value,
                maxWidth: 150,
            },
            {
                headerName: "Last Modified At",
                field: "modifiedAt",
                cellRenderer: DateCellRenderer,
                maxWidth: 200,
            },
            {
                headerName: "Last Modified By",
                field: "modifiedBy",
            },
            {
                headerName: "Date Added",
                field: "createdAt",
                cellRenderer: DateCellRenderer,
                sort: "desc",
                maxWidth: 200,
            },
            {
                headerName: "Created By",
                field: "createdBy",
            },
            {
                headerName: "Status",
                field: "status",
                resizable: false,
                cellRenderer: StatusCellRenderer,
                width: 100,
            },
            {
                headerName: "Deal ID",
                field: "dealId",
                minWidth: 70,
            },
        ],
        [DateCellRenderer],
    );

    const onGridReady = (event: GridReadyEvent) => {
        gridApi.current = event.api;
    };

    const defaultColDef = useMemo<ColDef>(
        () => ({
            minWidth: 200,
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
        }),
        [],
    );

    const getRowId: GetRowIdFunc<DealExtractionInfo> = React.useCallback(
        params =>
            isDealInfoWithParentTypeGuard(params.data)
                ? `${params.data.dealName}-${params.data.projectId}`
                : isExtractionInfoWithParentTypeGuard(params.data)
                  ? `${params.data.projectId}-${params.data.extractionId}`
                  : isAddFileRowTypeGuard(params.data)
                    ? `${params.data.projectId}-add-file`
                    : "",
        [],
    );

    const gridOptions = useMemo(
        () =>
            ({
                treeData: true,
                isGroupOpenByDefault: params => {
                    return (
                        params.rowNode?.data &&
                        isDealInfoWithParentTypeGuard(params.rowNode.data) &&
                        params.rowNode.data.numOfExtractions === 0
                    );
                },
                getDataPath: deal => {
                    if (isDealInfoWithParentTypeGuard(deal)) {
                        return [`${deal.dealName}-${deal.projectId}`];
                    }
                    if (isExtractionInfoWithParentTypeGuard(deal)) {
                        return [
                            `${deal.parentDealName}-${deal.projectId}`,
                            deal.sheetName,
                        ];
                    }
                    return null;
                },
                columnMenu: "legacy",
            }) as GridOptions<DealExtractionInfo>,
        [],
    );

    const isFullWidthRow = useCallback(
        (params: IsFullWidthRowParams<DealExtractionInfo>) => {
            return Boolean(
                params.rowNode?.data &&
                    isAddFileRowTypeGuard(params.rowNode.data),
            );
        },
        [],
    );
    const fullWidthCellRenderer = useCallback(UploadFileCellRenderer, []);

    const dealTableStyles = {
        ".delete-item": {
            color: "var(--proda-red) !important",
        },
    };

    const autoSaveGridState = (event: StateUpdatedEvent) => {
        const ignoreSources = [
            "rowGroupExpansion",
            "focusedCell",
            "version",
            "cellSelection",
            "scroll",
            "gridInitializing",
        ];
        if (event.sources.some(source => ignoreSources.includes(source))) {
            return;
        }
        const gridState = {
            ...event.state,
            rowGroupExpansion: undefined,
            scroll: undefined,
            cellSelection: undefined,
            focusedCell: undefined,
        };
        saveGridState(gridState);
    };

    const handleResetButtonClicked = () => {
        resetViewButtonClicked();
        gridApi.current?.resetColumnState();
        gridApi.current?.setFilterModel(null);
    };

    React.useEffect(() => {
        const resizeHandler = () => {
            gridApi.current?.sizeColumnsToFit();
        };
        window.addEventListener("resize", resizeHandler);
        return () => window.removeEventListener("resize", resizeHandler);
    }, []);

    React.useEffect(() => {
        if (suppressNoRowOverlay === true) {
            gridApi.current?.hideOverlay();
        }
    }, [suppressNoRowOverlay, rowData]);

    const selection = useMemo<SelectionOptions>(() => {
        return {
            mode: "multiRow",
            enableClickSelection: false,
            checkboxes: false,
            headerCheckbox: false,
        };
    }, []);

    return (
        <>
            <GlobalStyles styles={dealTableStyles} />
            <Box
                sx={{
                    ...containerStyle,
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                }}
            >
                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: "auto",
                    }}
                >
                    <Grid<DealExtractionInfo>
                        gridTheme={"ag-theme-custom-proda"}
                        rowData={rowData}
                        noRowsOverlayComponent={EmptyGridMessageRenderer}
                        noRowsOverlayComponentParams={
                            {
                                gridError: dealsIsError,
                                searchPhrase: dealSearchPhrase,
                                errorMessage: INTERNAL_ERROR_MESSAGE,
                                emptyMessage: GRID_EMPTY_MESSAGE,
                                emptySubMessage: GRID_EMPTY_SUBMESSAGE,
                            } as EmptyGridMessageRendererProps
                        }
                        initialState={savedGridState}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        gridOptions={gridOptions}
                        autoSizeStrategy={{ type: "fitGridWidth" }}
                        autoGroupColumnDef={{
                            headerName: "Project Name",
                            minWidth: 250,
                            cellRendererParams: {
                                suppressCount: true,
                                innerRenderer: DealNameCellRenderer,
                                innerRendererParams: {
                                    onViewDeal,
                                    onViewExtraction,
                                    onViewRentRoll,
                                    getDealUrl,
                                    getExtractionUrl,
                                    getRentRollExtractionUrl,
                                } as React.ComponentProps<
                                    typeof DealNameCellRenderer
                                >,
                            },
                            cellClass: ["ag-right-aligned-cell"],
                            pinned: "left",
                            valueGetter: params => {
                                const dealNameWithId = params.node?.key;
                                const parts = dealNameWithId?.split("-");
                                const allButLastPart =
                                    parts && parts.length > 1
                                        ? parts.slice(0, -1)
                                        : parts;
                                const dealName = allButLastPart?.join("-");
                                return dealName;
                            },
                            suppressMovable: true,
                            lockPosition: "left",
                        }}
                        selection={selection}
                        suppressHeaderFocus={true}
                        getContextMenuItems={getContextMenuItems}
                        onGridReady={onGridReady}
                        isFullWidthRow={isFullWidthRow}
                        fullWidthCellRenderer={fullWidthCellRenderer}
                        fullWidthCellRendererParams={{
                            handleAddFileButtonClicked: onAddFile,
                        }}
                        enterNavigatesVertically={true}
                        enterNavigatesVerticallyAfterEdit={true}
                        gridLoaderComponent={GridLoaderSkeleton}
                        onStateUpdated={autoSaveGridState}
                        getRowId={getRowId}
                    />
                </Box>
                <Box
                    sx={{
                        borderTop: "2px solid var(--proda-neon-blue-10)",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        flexShrink: 0,
                        backgroundColor: "var(--proda-white)",
                        boxSizing: "border-box",
                        gap: "1rem",
                        padding: "1.5rem 2rem",
                    }}
                >
                    {saveGridStatus === "resolved" && (
                        <Typography
                            variant="body3"
                            sx={{ display: "flex", alignItems: "center" }}
                            data-testid={testId}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    alignSelf: "center",
                                    width: "15px",
                                    height: "15px",
                                    borderRadius: "50%",
                                    backgroundColor: "var(--proda-green-120)",
                                    color: "white",
                                    marginRight: "0.5rem",
                                }}
                            >
                                <IconBox>
                                    <Icon icon="check" size="xs" />
                                </IconBox>
                            </Box>
                            View autosaved
                        </Typography>
                    )}
                    <Button
                        onClick={handleResetButtonClicked}
                        pattern="secondary"
                        size="large"
                        isInline={true}
                    >
                        Reset View
                    </Button>
                </Box>
            </Box>
        </>
    );
};
